import { Trans, useLingui } from "@lingui/react/macro";
import { Settings } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

import { AppModel } from "../../app/app-model";
import { ErrorPage } from "../../app/error-page";
import { PageHeaderActions } from "../../app/layout/layout-context";
import { Button } from "../../components/ui-kit/base/button";
import { Spinner } from "../../components/ui-kit/spinner";
import { assert } from "../../utils/utils";
import { PatientProfileHeader } from "./profile/patient-profile-header";

// Sub-route suffixes (relative to /patient/:id) that show the profile header.
const HEADER_SUFFIXES = [
  "",
  "/alerts",
  "/alerts/thresholds",
  "/lvfp",
  "/dri",
  "/afib",
  "/vitals",
  "/symptoms",
  "/events",
];

export const PatientRoute = observer(function PatientRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const { id } = useParams<{ id: string }>();
  assert(id !== undefined, "Patient ID param not found");

  const patientId = parseInt(id);
  const location = useLocation();
  const isProfilePage =
    location.pathname === `/patient/${patientId}` ||
    location.pathname === `/patient/${patientId}/`;
  const showHeader = HEADER_SUFFIXES.some((s) => {
    const p = `/patient/${patientId}${s}`;
    return location.pathname === p || location.pathname === `${p}/`;
  });
  const patient = useMemo(
    () => props.app.initPatient(patientId),
    [patientId, props.app],
  );

  useEffect(() => {
    const init = async () => {
      await patient.init();
    };

    void init();
  }, [patient]);

  if (!patient.loaded || patient.error) {
    return (
      <div className="flex h-full items-center justify-center flex-auto">
        {patient.error ? (
          <ErrorPage
            title={
              patient.error.status === 404
                ? t`Patient not found`
                : t`Error loading patient`
            }
            message={
              patient.error.status === 404
                ? t`Patient with ID ${patientId} does not exist.`
                : patient.error.message
            }
          />
        ) : (
          <Spinner />
        )}
      </div>
    );
  }

  return (
    <>
      {/* Sits in the shared page-title row (next to "Patient #… / <tab>")
          instead of taking its own row above the header. Shown on every tab
          that renders the header; the origin path is passed so "Back to
          profile" returns to the tab it was opened from. */}
      {showHeader && (
        <PageHeaderActions>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="text-foreground"
          >
            <Link
              to={`/patient/${patientId}/settings`}
              state={{ settingsFrom: location.pathname }}
            >
              <Settings className="size-4" />
              <Trans>Patient settings</Trans>
            </Link>
          </Button>
        </PageHeaderActions>
      )}
      {showHeader && (
        <PatientProfileHeader
          patient={patient}
          api={props.app.seerlinqApi}
          // On the profile page the collapse toggle moves into the chart tabs'
          // "measurements" row; elsewhere the header keeps its own toggle.
          showCollapseToggle={!isProfilePage}
        />
      )}
      <Outlet />
    </>
  );
});
